import { Injectable, Injector } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { DataTarget, getDataTarget, switchDataTarget } from '../_util/data-target';
import { DataService } from './data.service';

/** UI entry point for switching between the PROD and DEV database. Switching reloads the app. */
@Injectable({ providedIn: 'root' })
export class DataTargetService {
  /** Fixed for the lifetime of the page: a switch always reloads. */
  readonly current: DataTarget = getDataTarget();
  readonly other: DataTarget = this.current === 'PROD' ? 'DEV' : 'PROD';

  constructor(
    private confirmation: ConfirmationService,
    // Resolved lazily: DataService depends on services that start up before the login screen.
    private injector: Injector,
  ) {}

  /** Asks for confirmation (noting any unsynced local changes), then switches and reloads. */
  requestSwitch(to: DataTarget = this.other): void {
    if (to === this.current) return;

    const unsynced = this.countUnsyncedNotes();
    const lines = [`The app will reload and use the ${to} database instead of ${this.current}.`];
    if (unsynced > 0) {
      lines.push(
        `${unsynced} unsynced change${unsynced === 1 ? '' : 's'} in ${this.current} will stay on this device and sync when you switch back.`,
      );
    }

    this.confirmation.confirm({
      header: `Switch to ${to} database?`,
      message: lines.join(' '),
      icon: to === 'DEV' ? 'pi pi-exclamation-triangle' : 'pi pi-database',
      acceptLabel: `Switch to ${to}`,
      rejectLabel: 'Cancel',
      accept: () => switchDataTarget(to),
    });
  }

  private countUnsyncedNotes(): number {
    try {
      const notes = this.injector.get(DataService).allNotes ?? [];
      return notes.filter((n) => !!n.syncStatus && n.syncStatus !== 'synced').length;
    } catch {
      return 0;
    }
  }
}
